import { useTreatment } from '@splitsoftware/splitio-react';
import type { ValueOf } from 'type-fest';
import { useSelector } from 'react-redux';
import { featureFlags } from '../constants/feature-flags';
import useCompany from '../queries/company';
import { useIsStaffModeOn } from '../hooks/use-is-staff-mode-on';

function useFeatureFlag(name: ValueOf<typeof featureFlags>) {
  const { treatment, isReady } = useTreatment({ name });
  return isReady && treatment === 'on';
}

// Career app hooks
export function useHarmonizedSkillsAutocompleteFlag() {
  return useFeatureFlag(featureFlags.use_harmonized_skills_autocomplete);
}

export function useCareerPathSavedPathsFlag() {
  return useFeatureFlag(featureFlags.career_path_saved_desired_paths);
}

export function useCareerPathNewExplainabilityFlag() {
  return useFeatureFlag(featureFlags.isCareerPathNewExplainabilityFlag);
}

export function useOppGainedSKillsFlag() {
  return useFeatureFlag(featureFlags.isOppGainedSkillsEnabled);
}

export function useSkillsRatingLevelsDescriptionFlag() {
  const isFeatureFlagOn = useFeatureFlag(featureFlags.isSkillsRatingLevelsDescriptionEnabled);
  const isStaffModeOn = useIsStaffModeOn();
  return isStaffModeOn || isFeatureFlagOn;
}

export function useIsBulkActionsOn() {
  const isFeatureFlagOn = useFeatureFlag(featureFlags.enable_project_bulk_actions);
  const isStaffModeOn = useIsStaffModeOn();

  const isBulkActionsOn = isStaffModeOn || isFeatureFlagOn;
  return { isBulkActionsOn };
}

export function useJobBuilderFlag() {
  return useFeatureFlag(featureFlags.jobBuilder);
}

export function useStudioShowTableOfContentFlag() {
  const isFeatureFlagOn = useFeatureFlag(featureFlags.studioShowTableOfContent);
  const isStaffModeOn = useIsStaffModeOn();
  return isStaffModeOn || isFeatureFlagOn;
}

export function useStudioCoownerFlag() {
  return useFeatureFlag(featureFlags.studioCoOwner);
}

export function useStudioNewProgramTypesFlag() {
  const isFeatureFlagOn = useFeatureFlag(featureFlags.studioNewProgramTypes);
  const isStaffModeOn = useIsStaffModeOn();
  return isStaffModeOn || isFeatureFlagOn;
}

// Skills-foundation hooks
export function useDownloadProfileFlag() {
  return useFeatureFlag(featureFlags.skillsInventoryDownloadProfile);
}

export function useSkillPageV2() {
  return useFeatureFlag(featureFlags.skillsManagementV2);
}

export function useTasksFlag() {
  return useFeatureFlag(featureFlags.tasks);
}

export function useBulkEditFlag() {
  return useFeatureFlag(featureFlags.bulkEdit);
}

export function useCascadingFlag() {
  return useFeatureFlag(featureFlags.cascading);
}

export function useSkillComponentFlag() {
  return useFeatureFlag(featureFlags.skillComponent);
}

export function useAiIndexDashboardFlag() {
  const { data: { featureVariations: companyConfig } = {} } = useCompany();
  const signalIsOn = companyConfig?.signal?.isOn;
  return signalIsOn;
}

export function usePublicProfileFlag() {
  return useFeatureFlag(featureFlags.publicProfile);
}

export function useTalentPoolsFlag() {
  return useFeatureFlag(featureFlags.talentPools);
}

export function useSkillsInventoryFlag() {
  return useFeatureFlag(featureFlags.skillsInventory);
}

export function useMosaicV2Flag() {
  return useFeatureFlag(featureFlags.mosaicV2);
}

export function useIsWorkAvailable() {
  const isFeatureFlagOn = useFeatureFlag(featureFlags.mosaicV2);
  const isStaffModeOn = useIsStaffModeOn();

  return { isWorkAvailable: isStaffModeOn || isFeatureFlagOn };
}

export function useIsTasksAvailable() {
  const isFeatureFlagOn = useFeatureFlag(featureFlags.userTasks);
  const isStaffModeOn = useIsStaffModeOn();

  return { isTasksAvailable: isStaffModeOn || isFeatureFlagOn };
}

/**
 * Hook to check if the "new_profile_completeness_score" feature flag is on.
 */
export const useIsNewProfileCompleteness = () => {
  const isFeatureFlagOn = useFeatureFlag(featureFlags.new_profile_completeness_score);
  const isStaffModeOn = useIsStaffModeOn();

  return {
    isNewProfileCompletenessOn: isStaffModeOn || isFeatureFlagOn,
  };
};

export function useTaskAutomationAssistantFlag() {
  const isStaffModeOn = useIsStaffModeOn();
  const isFeatureFlagOn = useFeatureFlag(featureFlags.taskAutomationAssistant);
  return isStaffModeOn || isFeatureFlagOn;
}

export function useTaskAssistantRequestAgentFlag() {
  const isStaffModeOn = useIsStaffModeOn();
  const isFeatureFlagOn = useFeatureFlag(featureFlags.taskAssistantRequestAgent);
  return isStaffModeOn || isFeatureFlagOn;
}

export function useTaskAssistantContextualizedFlag() {
  const isStaffModeOn = useIsStaffModeOn();
  const isFeatureFlagOn = useFeatureFlag(featureFlags.taskAssistantContextualized);
  return isStaffModeOn || isFeatureFlagOn;
}

export function useAiIndexCostSavingFlag() {
  const isStaffModeOn = useIsStaffModeOn();
  const isFeatureFlagOn = useFeatureFlag(featureFlags.aiIndexCostSaving);
  return isStaffModeOn || isFeatureFlagOn;
}

export function useAiIndexAutomationStatusFlag() {
  const isStaffModeOn = useIsStaffModeOn();
  const isFeatureFlagOn = useFeatureFlag(featureFlags.aiIndexAutomationStatus);
  return isFeatureFlagOn || isStaffModeOn;
}

export const useIsNewNavigation = () => {
  const userScope = useSelector((state: { auth: { scope: { isNewNavigation: boolean } } }) => state?.auth?.scope);
  const isFeatureFlagOn = useFeatureFlag(featureFlags.newNavigation);

  const isNewNavigation = userScope?.isNewNavigation || isFeatureFlagOn;
  return { isNewNavigation };
};

/**
 * Hook to check if the "search" feature flag is set to v4.
 * Note: This checks for 'v4' value, not 'on' like other flags.
 */
export function useIsNewSearch() {
  const { treatment, isReady } = useTreatment({ name: featureFlags.search });
  return { isReady, isNewSearch: isReady && treatment === 'v4' };
}

export function useIsNewMatchingExplainabilityFlag() {
  return useFeatureFlag(featureFlags.is_new_matching_explainability);
}

export function useIsMosaicMatchSortingFlag() {
  return useFeatureFlag(featureFlags.mosaicMatchSorting);
}

export function usePublicProfileFeatureFlag() {
  const isFeatureFlagOn = useFeatureFlag(featureFlags.public_profile);
  const isStaffModeOn = useIsStaffModeOn();
  return {
    isReady: true,
    isPublicProfileOn: isStaffModeOn || isFeatureFlagOn,
  };
}

export function useIsProfileTooltipsFeatureFlag() {
  const isFeatureFlagOn = useFeatureFlag(featureFlags.isProfileTooltipsEnabled);
  const isStaffModeOn = useIsStaffModeOn();
  return {
    isProfileTooltipsEnabled: isStaffModeOn || isFeatureFlagOn,
  };
}

export function useMosaicUploadFilesFlag() {
  return useFeatureFlag(featureFlags.mosaicUploadFiles);
}

export function useMosaicUploadImageFlag() {
  return useFeatureFlag(featureFlags.mosaicUploadImage);
}

export function useMosaicNewUiFlag() {
  return useFeatureFlag(featureFlags.mosaicNewUi);
}

export function useNewFteCalculationFlag() {
  return useFeatureFlag(featureFlags.newFteCalculation);
}

export function useAddSkillToProfileExternalButtonFlag() {
  return useFeatureFlag(featureFlags.slAddSkillToProfile);
}

export function useOppsAddSkillToProfileFlag() {
  const isFeatureFlagOn = useFeatureFlag(featureFlags.oppsAddSkillToProfile);
  const isStaffModeOn = useIsStaffModeOn();
  return isStaffModeOn || isFeatureFlagOn;
}

export function useSLFindOpportunitiesFlag() {
  const isFeatureFlagOn = useFeatureFlag(featureFlags.SL_Find_opportunities);
  const isStaffModeOn = useIsStaffModeOn();
  return isStaffModeOn || isFeatureFlagOn;
}

export function useEnableCustomFieldLinksFlag() {
  return useFeatureFlag(featureFlags.enable_custom_field_links);
}

export function useShowLastSlotWarningFlag() {
  return useFeatureFlag(featureFlags.mosaicLastSlotWarning);
}

export function useProficiencyDescriptionCharacterLimitFlag() {
  const isFeatureFlagOn = useFeatureFlag(featureFlags.proficiencyDescriptionCharacterLimit);
  const isStaffModeOn = useIsStaffModeOn();
  return isStaffModeOn || isFeatureFlagOn;
}

export function useMosaicTalentPoolsFlag() {
  const isFeatureFlagOn = useFeatureFlag(featureFlags.mosaicTalentPools);
  const isStaffModeOn = useIsStaffModeOn();
  return isStaffModeOn || isFeatureFlagOn;
}

export function useAiIndexJobFunctionToggleFilterFlag() {
  const isFeatureFlagOn = useFeatureFlag(featureFlags.aiIndexJobFunctionToggleFilter);
  const isStaffModeOn = useIsStaffModeOn();
  return isStaffModeOn || isFeatureFlagOn;
}

export function useAgentTmpFlag() {
  return useFeatureFlag(featureFlags.agentTmp);
}
